import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Badge, Spinner, Table } from "@/components/ds";
import { AdminPage } from "@/components/app/admin/AdminPage";
import { usePageTitle } from "../hooks/usePageTitle";
import { TEMPLATE_NAMES } from "@/services/email";
import styles from "./AdminLogs.module.css";

interface EmailSendRecord {
  readonly id: string;
  readonly template: string;
  readonly recipient: string;
  readonly subject: string;
  readonly status: string;
  readonly provider: string;
  readonly providerMessageId: string | null;
  readonly orgId: string | null;
  readonly error: string | null;
  readonly sentAt: string;
}

type State =
  | { kind: "loading" }
  | { kind: "unauthorized" }
  | { kind: "ready"; emails: readonly EmailSendRecord[] }
  | { kind: "error"; message: string };

const TEMPLATE_OPTIONS = ["all", ...TEMPLATE_NAMES] as const;

function statusVariant(status: string): "success" | "warning" | "danger" | "neutral" {
  if (status === "sent") return "success";
  if (status === "rate_limited" || status === "dropped_allowlist") return "warning";
  if (status === "failed") return "danger";
  return "neutral";
}

export function AdminEmailsPage() {
  usePageTitle("Admin · Emails");
  const [state, setState] = useState<State>({ kind: "loading" });
  const [template, setTemplate] = useState<(typeof TEMPLATE_OPTIONS)[number]>("all");

  useEffect(() => {
    const params = new URLSearchParams();
    if (template !== "all") params.set("template", template);
    fetch(`/api/admin/emails?${params.toString()}`)
      .then(async (res) => {
        if (res.status === 404) { setState({ kind: "unauthorized" }); return; }
        if (!res.ok) { setState({ kind: "error", message: `Failed (${res.status})` }); return; }
        const json = await res.json() as { success: boolean; data?: { emails: EmailSendRecord[] } };
        if (json.success && json.data) setState({ kind: "ready", emails: json.data.emails });
        else setState({ kind: "error", message: "Unexpected response" });
      })
      .catch(() => setState({ kind: "error", message: "Network error" }));
  }, [template]);

  if (state.kind === "unauthorized") return <AdminPage title="Not found" showBack={false}><div /></AdminPage>;

  return (
    <AdminPage title="Emails">
      <div className={styles.filters}>
        <label className={styles.field}>
          Template
          <select
            value={template}
            onChange={(e) => setTemplate(e.target.value as (typeof TEMPLATE_OPTIONS)[number])}
            className={styles.input}
          >
            {TEMPLATE_OPTIONS.map((opt) => (<option key={opt} value={opt}>{opt}</option>))}
          </select>
        </label>
        <Link to="/admin/emails/templates" className={styles.rightLink}>
          Preview templates →
        </Link>
      </div>

      {state.kind === "loading" ? <Spinner />
      : state.kind === "error" ? <div className={styles.empty}>{state.message}</div>
      : state.emails.length === 0 ? <div className={styles.empty}>No emails.</div>
      : (
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Sent</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Template</Table.HeaderCell>
              <Table.HeaderCell>Recipient</Table.HeaderCell>
              <Table.HeaderCell>Subject</Table.HeaderCell>
              <Table.HeaderCell>Provider</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {state.emails.map((email) => (
              <Table.Row key={email.id}>
                <Table.Cell>{new Date(email.sentAt).toLocaleString()}</Table.Cell>
                <Table.Cell><Badge variant={statusVariant(email.status)}>{email.status}</Badge></Table.Cell>
                <Table.Cell>{email.template}</Table.Cell>
                <Table.Cell>{email.recipient}</Table.Cell>
                <Table.Cell>{email.subject}</Table.Cell>
                <Table.Cell>
                  {email.providerMessageId && email.provider === "resend" ? (
                    <a
                      href={`https://resend.com/emails/${email.providerMessageId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.link}
                    >
                      {email.providerMessageId.slice(0, 8)}…
                    </a>
                  ) : (
                    <span className={styles.mono}>{email.provider}</span>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </AdminPage>
  );
}
